import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { PropertyDetail as Detail } from "@screener/contracts";
import {
  RISK_LABEL_KO,
  checklistNewUrl,
  formatWonKo,
  studyStageUrl,
  toWon,
  type HardFilterResult,
} from "@screener/domain";
import { api, ApiFailure } from "../lib/api";
import SampleBanner from "../components/SampleBanner";

const VERDICT_STYLE: Record<string, string> = {
  exclude: "border-stop/30 bg-stop-soft text-stop",
  review: "border-warn/30 bg-warn-soft text-warn",
  pass: "border-line bg-surface text-ink-soft",
};

const VERDICT_LABEL: Record<string, string> = {
  exclude: "제외 — 이 물건은 보지 않는다",
  review: "조건부 검토 — 확인되어야 풀린다",
  pass: "하드필터 통과",
};

interface Loaded {
  property: Detail;
  hardFilter: HardFilterResult;
}

export default function PropertyDetail() {
  const { id = "" } = useParams();
  const [data, setData] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .property(id)
      .then((d) => alive && setData(d))
      .catch((e: unknown) =>
        alive
          ? setError(e instanceof ApiFailure ? e.message : "물건을 불러오지 못했다")
          : undefined,
      );
    return () => {
      alive = false;
    };
  }, [id]);

  if (error) {
    return (
      <div>
        <Link to="/properties" className="text-[13px] text-cobalt">
          물건 목록으로
        </Link>
        <p className="mt-5 rounded-2xl border border-stop/30 bg-stop-soft px-4 py-3 text-[13px] text-stop">
          {error}
        </p>
      </div>
    );
  }
  if (!data) return <p className="text-[13px] text-ink-soft">불러오는 중</p>;

  const p = data.property;
  const hardFilter = data.hardFilter;
  const saleDate = p.currentRound?.scheduledAt?.slice(0, 10) ?? null;
  const blocking = hardFilter.outcomes.filter((o) => o.verdict !== "pass");

  return (
    <div>
      {p.dataMode === "sample" ? <SampleBanner /> : null}
      <Link to="/properties" className="text-[13px] text-cobalt">
        물건 목록으로
      </Link>

      <h1 className="mt-4 text-[26px] font-extrabold tracking-tight">{p.caseLabel}</h1>
      <p className="mt-1 text-[14px] text-ink-soft">
        {p.courtName} · 물건번호 {p.itemNumber}
      </p>
      <p className="mt-3 text-[15px]">{p.addressOriginal}</p>

      <div
        className={`mt-6 rounded-[24px] border px-5 py-4 ${VERDICT_STYLE[hardFilter.verdict]}`}
      >
        <p className="text-[15px] font-bold">{VERDICT_LABEL[hardFilter.verdict]}</p>
        <ul className="mt-2 space-y-1 text-[13px] leading-relaxed">
          {blocking.length === 0 ? (
            <li>확인된 하드필터 항목이 없다.</li>
          ) : (
            blocking.map((o) => (
              <li key={o.riskType}>
                <a href={studyStageUrl(o.riskType)} className="underline">
                  {RISK_LABEL_KO[o.riskType]}
                </a>
                {" — "}
                {o.reason}
              </li>
            ))
          )}
        </ul>
      </div>

      {p.currentRound ? (
        <div className="mt-6 rounded-[24px] border border-line p-5">
          <h2 className="text-[15px] font-bold">현재 회차</h2>
          <dl className="mt-3 grid grid-cols-2 gap-y-2 text-[14px]">
            <dt className="text-ink-soft">감정가</dt>
            <dd className="text-right font-semibold">
              {formatWonKo(toWon(p.currentRound.appraisalAmount))}
            </dd>
            <dt className="text-ink-soft">최저매각가</dt>
            <dd className="text-right font-semibold">
              {formatWonKo(toWon(p.currentRound.minimumBidAmount))}
            </dd>
            <dt className="text-ink-soft">유찰</dt>
            <dd className="text-right">{p.currentRound.failedCount}회</dd>
            <dt className="text-ink-soft">매각기일</dt>
            <dd className="text-right">{saleDate ?? "확인 안 됨"}</dd>
          </dl>
        </div>
      ) : null}

      <a
        href={checklistNewUrl({
          label: `${p.caseLabel} ${p.buildingName ?? ""}`.trim(),
          court: p.courtName,
          saleDate,
          propertyId: p.id,
        })}
        className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-ink px-6 py-3.5 text-[14px] font-semibold text-white transition hover:opacity-90"
      >
        실전 체크리스트로 점검하기
      </a>
      <p className="mt-2 text-center text-[12px] text-ink-soft">
        낙찰로드의 체크리스트가 사건번호, 법원, 기일이 채워진 상태로 열립니다.
      </p>
    </div>
  );
}
